/* The presentation model for D-17. Pure functions, no React, no fetch.
 *
 * **Two things on this screen are statements about a customer's alerting, so they are derived here
 * and tested.**
 *
 * `declared` is the first. `false` means the rules below are the *platform's* default — critical
 * incidents, by email, to the destination the operator configured — and nobody has chosen. Rendering
 * that as a configured policy would present a default as somebody's decision, which is the
 * distinction the API goes out of its way to send.
 *
 * `coverage` is the second and the more useful one. A policy can be declared, well-formed, stored,
 * and *not route a critical incident anywhere* — a tenant who declared "info volume anomalies to a
 * webhook" and nothing else has silently stopped being paged about a dropped column. The API cannot
 * say that (a rule is valid in isolation) and the screen must, because the failure mode is silence.
 */

import type {
  IncidentKind,
  NotificationChannel,
  NotificationRule,
  NotificationTargetKind,
  RoutingPolicy,
  Severity,
} from '@/shared/api/generated/observability';

/** Severity as an order, mirroring the backend's `_SEVERITY_RANK`.
 *
 * Declared rather than derived from the string, for the reason the backend states: alphabetically
 * 'critical' < 'info' < 'warning', so a text comparison would order a blocker first by luck and be
 * wrong for every other pair. Total, so a fourth member is a compile error here rather than a floor
 * that silently admits it.
 */
const SEVERITY_RANK = {
  info: 0,
  warning: 1,
  critical: 2,
} satisfies Record<Severity, number>;

/** Whether a rule would send an incident of this severity. Kind is a separate question. */
export function admitsSeverity(rule: NotificationRule, severity: Severity): boolean {
  if (!rule.enabled) return false;
  return SEVERITY_RANK[severity] >= SEVERITY_RANK[rule.min_severity];
}

/** Whether a rule would send an incident of this kind. `null` on the rule means every kind. */
export function admitsKind(rule: NotificationRule, kind: IncidentKind): boolean {
  return rule.incident_kind === null || rule.incident_kind === kind;
}

export type Coverage = 'critical-covered' | 'critical-uncovered' | 'silenced';

/** Whether anything at all would reach somebody about a critical incident.
 *
 * **The one derived judgement on this screen, and the reason it exists is that its failure is
 * silence.** A declared policy that routes only INFO volume anomalies is valid, stored, and rendered
 * as a healthy list of rules — while a dropped column in the payments ledger reaches nobody. Neither
 * the API nor a per-rule badge can say that, because every rule is fine on its own.
 *
 * `silenced` is separated from `critical-uncovered` because they are different acts. An empty
 * declared policy is somebody deliberately turning notification off, and a screen that called that
 * an oversight would be arguing with a decision. A policy with rules that happen to miss criticals is
 * far more likely to be an accident, and it reads differently.
 */
export function coverageOf(policy: RoutingPolicy): Coverage {
  if (policy.declared && policy.rules.length === 0) return 'silenced';
  /* **Severity only, and deliberately no kind clause.** The first draft here also tested
   * `admitsKind(rule, rule.incident_kind)`, which is tautological — a rule always admits its own
   * kind — so it read as a check and was dead code. This function answers the coarse question, "does
   * *anything* reach somebody about a critical incident", and `uncoveredKinds` answers the useful
   * one per kind. Keeping the two apart is what lets the screen say "covered for schema drift, not
   * for a freshness breach" instead of one boolean that is true on the strength of one rule. */
  const covered = policy.rules.some((rule) => admitsSeverity(rule, 'critical'));
  return covered ? 'critical-covered' : 'critical-uncovered';
}

/** Which kinds a critical incident would NOT reach anybody for. Empty when every kind is covered.
 *
 * Per kind rather than one boolean, because "we are covered for schema drift and not for a freshness
 * breach" is the realistic shape of a partly-declared policy and is exactly what a reader needs. A
 * single "covered" flag would report the whole estate as fine on the strength of one rule.
 */
export function uncoveredKinds(
  policy: RoutingPolicy,
  kinds: readonly IncidentKind[],
): readonly IncidentKind[] {
  if (policy.declared && policy.rules.length === 0) return [];
  return kinds.filter(
    (kind) =>
      !policy.rules.some(
        (rule) => admitsSeverity(rule, 'critical') && admitsKind(rule, kind),
      ),
  );
}

export type RuleTone = 'ok' | 'info' | 'warn' | 'neutral';

/* **No `unknown` member and no `??` fallback, because `satisfies` already makes both maps total.**
 * eslint refused the fallback as unreachable, which is the right answer: an added channel or target
 * kind is a compile error in this file rather than a badge rendered in the wrong hue. The pattern to
 * avoid is the opposite one — a `switch` with a `default:` — which is what §9 records `verdictTone`
 * getting wrong in `core.ai.evals`, where a fifth verdict silently took the *permitted* tone. */

const CHANNEL_TONE = {
  email: 'info',
  webhook: 'info',
} satisfies Record<NotificationChannel, RuleTone>;

/** The tone for a destination.
 *
 * `deployment_default` is `neutral` — it is what every install already does, and colouring it would
 * make the ordinary case look like a finding. A tenant-supplied destination is `warn`, and that is
 * not an accusation: it is the row an access review looks at, because it is the one where a
 * customer's incident metadata leaves this deployment's own configured destination.
 *
 * Total via `satisfies`, so a fourth target kind is a compile error rather than an unstyled badge.
 */
const TARGET_TONE = {
  deployment_default: 'neutral',
  email_recipients: 'warn',
  webhook_url: 'warn',
} satisfies Record<NotificationTargetKind, RuleTone>;

export function channelTone(channel: NotificationChannel): RuleTone {
  return CHANNEL_TONE[channel];
}

export function targetTone(kind: NotificationTargetKind): RuleTone {
  return TARGET_TONE[kind];
}

/** A stable React key for a rule.
 *
 * The id, and only the id. The default policy's rule carries the nil UUID and there is exactly one
 * of them, so it is unique; a composite key built from channel and severity would collide the moment
 * a tenant declared two rules differing only by destination.
 */
export function keyOf(rule: NotificationRule): string {
  return rule.id;
}

/** Whether this rule is the platform's default rather than a stored row.
 *
 * The nil UUID, matching `RoutingPolicy.default_for`. A default rule has no author, cannot be
 * withdrawn, and must not be offered a Withdraw button — a control that 404s is worse than no
 * control, because the reader concludes the platform is broken rather than that there is nothing
 * to remove.
 */
export const NIL_UUID = '00000000-0000-0000-0000-000000000000';

export function isDefaultRule(rule: NotificationRule): boolean {
  return rule.id === NIL_UUID;
}

/* ---------------------------------------------------------------- what the screen states

 * Where one rule actually sends, as a reader would quote it.
 *
 * `NotificationRule.destination` is a host-written sentence — "the deployment's configured
 * destination", "1 address" — which is right for an audit log and wrong for the coverage readout,
 * where the question is *which mailbox*. A tenant-supplied target has the real value on the wire
 * (returned as declared, because a screen that cannot show what it stored cannot let anybody check
 * it), so that is what this returns; the deployment default has no address to show and keeps the
 * sentence.
 */
export function destinationOf(rule: NotificationRule): string {
  if (rule.target_kind === 'email_recipients') return rule.recipients.join(', ');
  if (rule.target_kind === 'webhook_url' && rule.webhook_url !== null)
    return rule.webhook_url;
  return rule.destination;
}

/** Whether a critical incident of this kind reaches anybody, and through which rules.
 *
 * **Per kind, not one flag**, which is the whole argument for the section: a policy is realistically
 * covered for schema drift and not for a freshness breach, and a single boolean would report the
 * estate as fine on the strength of one rule. `uncoveredKinds` answers the same question as a list
 * of gaps; this answers it per kind *with the evidence*, because "covered" with no destination
 * beside it is a claim a reader cannot check.
 */
export interface KindCoverage {
  readonly kind: IncidentKind;
  readonly covered: boolean;
  /** The destinations that would be reached, in declaration order. Empty when uncovered. */
  readonly destinations: readonly string[];
  /** How many rules admit it — a count as well as the list, because two rules to one mailbox is one
   * intent declared twice and the server deduplicates destinations at send time. */
  readonly ruleCount: number;
}

export function coveragePerKind(
  policy: RoutingPolicy,
  kinds: readonly IncidentKind[],
): readonly KindCoverage[] {
  return kinds.map((kind) => {
    const hitting = policy.rules.filter(
      (rule) => admitsSeverity(rule, 'critical') && admitsKind(rule, kind),
    );
    return {
      kind,
      covered: hitting.length > 0,
      destinations: hitting.map((rule) => destinationOf(rule)),
      ruleCount: hitting.length,
    };
  });
}

/** The headline above the table: which of four things is true, and in what tone.
 *
 * Four states rather than a boolean, and each is a different conversation. `silenced` is a decision
 * on record; `no-critical-route` is almost certainly an accident; `partial` is the realistic shape
 * of a half-declared policy; `covered` is the only one that is good news. Collapsing any pair would
 * make the screen either alarming about a choice or quiet about a gap.
 */
export type CoverageState = 'silenced' | 'no-critical-route' | 'partial' | 'covered';

export interface CoverageSummary {
  readonly state: CoverageState;
  readonly tone: 'ok' | 'warn' | 'error';
  /** The kinds nobody would be told about. Only non-empty for `partial`. */
  readonly uncovered: readonly IncidentKind[];
}

export function coverageSummary(
  policy: RoutingPolicy,
  kinds: readonly IncidentKind[],
): CoverageSummary {
  const coverage = coverageOf(policy);
  if (coverage === 'silenced') return { state: 'silenced', tone: 'warn', uncovered: [] };
  if (coverage === 'critical-uncovered') {
    return { state: 'no-critical-route', tone: 'error', uncovered: [] };
  }
  const uncovered = uncoveredKinds(policy, kinds);
  return uncovered.length > 0
    ? { state: 'partial', tone: 'warn', uncovered }
    : { state: 'covered', tone: 'ok', uncovered: [] };
}

/** What a severity floor does in practice, said as a second line under its name.
 *
 * "Warning and above" is the floor's name and "Warning and critical" is what it does — and the
 * second is what a reader is checking. Two lines rather than one longer label, because the first is
 * what they scan for and the second is what they read once.
 */
export function severityNoteKey(severity: Severity): string {
  return `routing.severityNote.${severity}`;
}

/** Whether this rule sends anything at all. A disabled rule stays on the policy and routes nothing. */
export function isSilenced(rule: NotificationRule): boolean {
  return !rule.enabled;
}

/* ---------------------------------------------------------------- the replay

 * What these rules would have done to the incidents already on the queue.
 *
 * **Against the rules as they stand now, not the rules in force when each incident opened.** That is
 * a real limitation rather than a shortcut: this platform records the routing *decision* per rule
 * and exposes no per-incident delivery record, so a true history is unavailable and would have to be
 * invented. The screen says which of the two it is showing, because a reader who took this for a
 * delivery log would be reading a claim nothing behind it supports.
 *
 * It is the most useful thing on the screen all the same: a steward changing a rule can see, against
 * incidents they recognise, who would now be told — which is exactly what the coverage banners
 * answer in the abstract.
 */
export interface Replay {
  readonly incidentId: string;
  readonly severity: Severity;
  readonly kind: IncidentKind;
  /** Deduplicated, because two rules pointing at one mailbox send once — `routes_for` dedupes on
   * (channel, destination) at the source, and a screen listing it twice would predict two alerts. */
  readonly reached: readonly string[];
}

export interface ReplaySubject {
  readonly id: string;
  readonly severity: Severity;
  readonly kind: IncidentKind;
}

export function replayAgainst(
  policy: RoutingPolicy,
  incidents: readonly ReplaySubject[],
): readonly Replay[] {
  return incidents.map((incident) => {
    const hitting = policy.rules.filter(
      (rule) =>
        admitsSeverity(rule, incident.severity) && admitsKind(rule, incident.kind),
    );
    return {
      incidentId: incident.id,
      severity: incident.severity,
      kind: incident.kind,
      reached: [...new Set(hitting.map((rule) => destinationOf(rule)))],
    };
  });
}

/* ---------------------------------------------------------------- the declare form

 * Whether the form can be submitted, and the sentence under it.
 *
 * The validity rule mirrors what the server refuses rather than guessing at it: an email rule with a
 * tenant destination needs at least one address, and a webhook rule needs an `https` URL — the
 * domain refuses `http` because a webhook carries incident metadata off the deployment. Everything
 * else the server may still refuse, and this deliberately does not try to predict it: a destination
 * outside the operator's allow-list is a 422 naming the variable, and guessing at an allow-list the
 * browser cannot see would either block a legitimate rule or promise one that fails.
 */
export interface DraftRule {
  readonly channel: NotificationChannel;
  readonly severity: Severity;
  /** Empty means every kind — the broadest rule, not a missing setting. */
  readonly kind: string;
  readonly targetKind: NotificationTargetKind;
  readonly recipients: string;
  readonly webhookUrl: string;
}

export const EMPTY_DRAFT: DraftRule = {
  channel: 'email',
  severity: 'critical',
  kind: '',
  targetKind: 'deployment_default',
  recipients: '',
  webhookUrl: '',
};

export function recipientsOf(draft: DraftRule): readonly string[] {
  return draft.recipients
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

export function draftIsValid(draft: DraftRule, allowedDomain: string | null): boolean {
  if (draft.targetKind === 'email_recipients') {
    const recipients = recipientsOf(draft);
    if (recipients.length === 0) return false;
    /* **The tenancy's own domain, checked here too — and the server checks it regardless.** This is
       not the enforcement; `NotificationRoutingService` is, and it reads the domain from
       `platform.tenant` rather than from anything a browser sent. Checking it here only means a
       reader is told before they submit rather than after, which is the whole reason the domain is
       on the wire. `null` refuses every tenant-supplied address, matching the server: it means no
       domain is on record, which is a different thing from "any domain". */
    if (allowedDomain === null) return false;
    return addressesOutside(allowedDomain, recipients).length === 0;
  }
  if (draft.targetKind === 'webhook_url') {
    return /^https:\/\/\S+$/.test(draft.webhookUrl.trim());
  }
  return true;
}

/** The domain part of an address, case-folded, or `null` when there is not exactly one.
 *
 * **A second implementation of the server's `email_domain_of`, and the duplication is deliberate
 * rather than overlooked.** The browser cannot call Python, and the alternative — submitting and
 * rendering the refusal — is the experience this change exists to avoid. What keeps the two from
 * drifting into disagreement is that this one is *advisory only*: it can only ever refuse early, and
 * the server refuses independently from a value no client supplied. If they disagree, the server
 * wins and the reader sees its message.
 */
function emailDomainOf(address: string): string | null {
  const trimmed = address.trim();
  const at = trimmed.lastIndexOf('@');
  if (at <= 0) return null;
  const domain = trimmed.slice(at + 1).toLowerCase();
  return domain === '' || !domain.includes('.') ? null : domain;
}

/** Which recipients the tenancy's domain does not admit, in the order given.
 *
 * Whole-label and case-folded, never a suffix: a `bank.com` tenancy must not admit `evil-bank.com`,
 * and a subdomain is a different mail domain. The same rule the server applies, for the same reason.
 */
export function addressesOutside(
  allowedDomain: string,
  recipients: readonly string[],
): readonly string[] {
  const wanted = allowedDomain.toLowerCase();
  return recipients.filter((address) => emailDomainOf(address) !== wanted);
}

/** Which destinations this channel can carry. A destination belongs to a channel — the API refuses a
 * webhook URL on an email rule and the domain makes it unrepresentable — so the dropdown offers only
 * what the server will accept rather than letting somebody compose a request it refuses. */
export function targetsFor(
  channel: NotificationChannel,
): readonly NotificationTargetKind[] {
  return channel === 'email'
    ? ['deployment_default', 'email_recipients']
    : ['deployment_default', 'webhook_url'];
}

/** What the form's footer says: the blocking reason, or the rule read back in plain words.
 *
 * A sentence rather than a validation badge, because the useful thing to show somebody about to
 * declare a routing rule is the rule itself in prose — "sends criticals of every kind to
 * dq-oncall@bank.in" is what catches a wrong dropdown, where a green tick does not.
 *
 * `values` carries copy **keys** for the parts that are vocabulary and literal text for the parts
 * that are the tenant's own, so the model stays free of `t` and one place decides how a severity or
 * a kind is named. `literal` says which is which, and it exists because the raw-key defect this
 * screen already shipped once came from a caller that could not tell them apart.
 */
export interface DraftHint {
  readonly kind: 'blocked' | 'summary';
  readonly key: string;
  readonly keyed: Readonly<Record<string, string>>;
  readonly literal: Readonly<Record<string, string>>;
}

export function draftHint(draft: DraftRule, allowedDomain: string | null): DraftHint {
  /* **The missing domain comes first, before "type a recipient".** If no domain is on record then
     no address can be accepted whatever they type, and asking for one first invites somebody to
     compose an address that can never be admitted. Found by a test that expected the other order. */
  if (draft.targetKind === 'email_recipients' && allowedDomain === null) {
    return { kind: 'blocked', key: 'routing.form.noDomain', keyed: {}, literal: {} };
  }
  if (draft.targetKind === 'email_recipients' && recipientsOf(draft).length === 0) {
    return { kind: 'blocked', key: 'routing.form.needRecipient', keyed: {}, literal: {} };
  }
  if (draft.targetKind === 'email_recipients') {
    const outside = addressesOutside(allowedDomain ?? '', recipientsOf(draft));
    if (outside.length > 0) {
      /* The offending addresses, named. A reader told "one of these four is not allowed" has to check
         four, and the one they mistyped is the one they will read past. */
      return {
        kind: 'blocked',
        key: 'routing.form.outsideDomain',
        keyed: {},
        literal: { domain: allowedDomain ?? '', addresses: outside.join(', ') },
      };
    }
  }
  if (draft.targetKind === 'webhook_url' && !draftIsValid(draft, allowedDomain)) {
    return { kind: 'blocked', key: 'routing.form.needHttps', keyed: {}, literal: {} };
  }

  const keyed: Record<string, string> = {
    sends: `routing.form.sends.${draft.severity}`,
    of: draft.kind === '' ? 'routing.form.everyKind' : `routing.kindLower.${draft.kind}`,
  };
  const literal: Record<string, string> = {};
  if (draft.targetKind === 'deployment_default') {
    keyed['to'] = 'routing.form.theDefault';
  } else {
    literal['to'] =
      draft.targetKind === 'email_recipients'
        ? recipientsOf(draft).join(', ')
        : draft.webhookUrl.trim();
  }
  return { kind: 'summary', key: 'routing.form.summary', keyed, literal };
}
